import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, Star, ChevronDown, Filter, 
  CheckCircle, Clock, XCircle, Loader 
} from 'lucide-react';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const FeedbackManagement = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [response, setResponse] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchFeedbacks();
  }, [filters]);

  const fetchFeedbacks = async () => {
    try {
      const queryParams = new URLSearchParams({
        ...filters
      }).toString();
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/feedback/college`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch feedbacks');
      
      const data = await response.json();
      setFeedbacks(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponseSubmit = async (feedbackId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/feedback/${feedbackId}/respond`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          responseMessage: response,
          status: 'resolved'
        })
      });

      if (!res.ok) throw new Error('Failed to submit response');

      const updatedFeedback = await res.json();
      setFeedbacks(prev => prev.map(f => 
        f._id === updatedFeedback._id ? updatedFeedback : f
      ));
      setResponse('');
      setSelectedFeedback(null);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      read: 'bg-blue-100 text-blue-800',
      inProgress: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'inProgress':
        return <Clock className="w-4 h-4" />;
      case 'read':
        return <MessageCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Feedback Management</h1>
          <p className="mt-2 text-gray-600">Manage and respond to alumni feedback</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search feedback..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 
                  focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 
                focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="read">Read</option>
              <option value="inProgress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>

           
          </div>
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <motion.div
                key={feedback._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {feedback.subject}
                      </h3>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          getStatusColor(feedback.status)
                        }`}>
                          {getStatusIcon(feedback.status)}
                          <span className="ml-2">{feedback.status}</span>
                        </span>
                     
                      
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {formatDate(feedback.createdAt)}
                    </span>
                  </div>

                  <p className="text-gray-600 mb-4">{feedback.message}</p>

                  {feedback.isAnonymous ? (
                    <p className="text-sm text-gray-500">Anonymous Feedback</p>
                  ) : (
                    <p className="text-sm text-gray-500">
                      From: {feedback.alumni?.firstName} {feedback.alumni?.lastName}
                    </p>
                  )}

                  {feedback.response?.message ? (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Response:</h4>
                      <p className="text-gray-600">{feedback.response.message}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Responded on: {formatDate(feedback.response.respondedAt)}
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSelectedFeedback(feedback)}
                      className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
                    >
                      Add Response
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Response Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <h3 className="text-xl font-semibold mb-4">Respond to Feedback</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Response
              </label>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 
                  focus:ring-blue-500 focus:border-transparent min-h-[150px]"
                placeholder="Type your response here..."
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setSelectedFeedback(null);
                  setResponse('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResponseSubmit(selectedFeedback._id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                  font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Submit Response
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;